import React, { useEffect, useRef, useState } from "react";
import { Enums, getRenderingEngine } from "@cornerstonejs/core";

// SCThumbnail: Cornerstone-based DICOM thumbnail renderer using viewport (same as main)
const SCThumbnail = ({ imageId, size = 40, seriesName = "SC Series", renderingEngineId = "ai-rendering-engine" }) => {
  const elementRef = useRef(null);
  const viewportIdRef = useRef(`sc-thumb-${Math.random().toString(36).substr(2, 9)}`);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const renderThumbnail = async () => {
      if (!elementRef.current || !imageId) {
        console.warn("SCThumbnail: Missing ref or imageId", { hasRef: !!elementRef.current, imageId });
        return;
      }

      try {
        console.log("SCThumbnail: Starting render for imageId:", imageId);

        // ✓ Global metadata provider already registered in Cornerstone initialization
        // Prevents duplicate registrations

        // Use the same rendering engine as main viewport
        let renderingEngine = getRenderingEngine(renderingEngineId);
        
        if (!renderingEngine) {
          console.error("SCThumbnail: Rendering engine not available");
          setError("Engine not available");
          return;
        }

        console.log("SCThumbnail: Rendering engine found, creating viewport");

        const viewportInput = {
          viewportId: viewportIdRef.current,
          type: Enums.ViewportType.STACK,
          element: elementRef.current,
          defaultOptions: { 
            background: [0, 0, 0],
            orientation: Enums.OrientationAxis.AXIAL,
          },
        };

        // Enable element on the rendering engine
        try {
          renderingEngine.enableElement(viewportInput);
          console.log("SCThumbnail: Element enabled");
        } catch (enableErr) {
          console.error("SCThumbnail: Error enabling element:", enableErr);
          throw enableErr;
        }

        const viewport = renderingEngine.getViewport(viewportIdRef.current);
        
        if (!viewport) {
          console.error("SCThumbnail: Viewport not retrieved");
          setError("Viewport not created");
          return;
        }

        console.log("SCThumbnail: Viewport retrieved, setting stack");

        // Set stack with single image and render (same as main viewport)
        try {
          await viewport.setStack([imageId], 0);
          console.log("SCThumbnail: Stack set, resizing viewport");
          
          // Resize viewport to ensure proper rendering with correct canvas size
          viewport.resize();
          
          // Reset camera to fit image properly
          const imageData = viewport.getImageData();
          if (imageData) {
            viewport.resetCamera();
          }
          
          viewport.render();
          
          setIsLoaded(true);
          setError(null);
          console.log("SCThumbnail: Rendered successfully");
        } catch (stackErr) {
          console.error("SCThumbnail: Error setting stack or rendering:", stackErr);
          setError(stackErr.message);
        }
      } catch (err) {
        console.error("SCThumbnail: Failed to render thumbnail:", err);
        setError(err.message);
        setIsLoaded(false);
      }
    };

    renderThumbnail();

    return () => {
      try {
        const renderingEngine = getRenderingEngine(renderingEngineId);
        if (renderingEngine) {
          console.log("SCThumbnail: Cleaning up viewport", viewportIdRef.current);
          renderingEngine.disableElement(viewportIdRef.current);
        }
      } catch (e) {
        console.error("SCThumbnail: Error during cleanup:", e);
      }
    };
  }, [imageId, renderingEngineId]);

  return (
    <div
      ref={elementRef}
      style={{
        width: size,
        height: size,
        background: "#1a1a1a",
        borderRadius: 2,
        marginRight: 8,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: isLoaded ? "1px solid #555" : "1px dashed #666",
        cursor: "pointer",
        position: "relative",
        overflow: "hidden",
        flexShrink: 0,
      }}
      title={error ? `Error: ${error}` : isLoaded ? seriesName : "Loading..."}
    >
      {error && (
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#f00",
          fontSize: 8,
          textAlign: "center",
          padding: 2,
          zIndex: 10,
        }}>
          {error.substring(0, 20)}
        </div>
      )}
    </div>
  );
};

export default SCThumbnail;
